// Assistant message row: small avatar + left-aligned bubble.
//
// Bubble content routes to:
//   - ScreenshotPart for the screenshot the model saw on this turn (image part)
//   - MarkdownText for assistant_text (code blocks, inline code, lists)
//   - ToolCallPart for every tool-call → ActionCard, with SafetyConfirmAction
//     inline above when the action is pending a safety decision.
//
// SafetyConfirmAction self-suppresses when its action isn't pending, so we
// always forward to both renderers.

import React, { useEffect, useState } from "react";
import { MessagePrimitive } from "@assistant-ui/react";
import { Sparkles, X } from "lucide-react";
import { ActionCard } from "../tools/ActionCard";
import { SafetyConfirmAction } from "../tools/SafetyConfirmAction";
import { useCopilotController } from "../CopilotRuntimeProvider";
import { MarkdownText } from "./MarkdownText";

function ToolCallPart({ toolCallId, toolName, args, status, result }) {
  const controller = useCopilotController();
  return (
    <>
      <SafetyConfirmAction
        toolCallId={toolCallId}
        args={args}
        status={status}
        result={result}
        controller={controller}
      />
      <ActionCard toolName={toolName} args={args} status={status} result={result} />
    </>
  );
}

// Screenshot the agent captured for this turn. Thumbnail in the bubble; click
// opens a full-size lightbox so the user can inspect what the model saw.
function ScreenshotPart({ image }) {
  const [zoomed, setZoomed] = useState(false);
  useEffect(() => {
    if (!zoomed) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setZoomed(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomed]);
  return (
    <>
      <button
        type="button"
        className="copilot-msg-shot"
        onClick={() => setZoomed(true)}
        aria-label="Open screenshot full size"
      >
        <img src={image} alt="Agent screenshot" />
      </button>
      {zoomed && (
        <div
          className="copilot-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Screenshot"
          onClick={() => setZoomed(false)}
        >
          <img src={image} alt="Agent screenshot" onClick={(e) => e.stopPropagation()} />
          <button
            type="button"
            className="copilot-lightbox__close"
            onClick={() => setZoomed(false)}
            aria-label="Close"
          >
            <X className="tw-h-4 tw-w-4" />
          </button>
        </div>
      )}
    </>
  );
}

export function AssistantMessage() {
  // ExternalStoreRuntime injects a phantom "running" assistant message at
  // the end of the thread while `isRunning` is true (it's how the library
  // knows where to slot streaming content). With our externally-driven
  // turn model, that phantom has no content and would render as an empty
  // bubble. `If hasContent` skips it; the ThinkingBubble owns the in-flight
  // signal instead.
  return (
    <MessagePrimitive.If hasContent>
      <MessagePrimitive.Root className="tw-flex tw-w-full tw-gap-2 tw-py-1">
        <div className="tw-mt-0.5 tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-accent tw-text-accent-foreground tw-shadow-sm">
          <Sparkles className="tw-h-3.5 tw-w-3.5" />
        </div>
        <div className="tw-min-w-0 tw-max-w-[85%] tw-overflow-hidden tw-rounded-2xl tw-rounded-bl-md tw-bg-card tw-px-3.5 tw-py-2 tw-text-sm tw-text-card-foreground tw-shadow-sm">
          <MessagePrimitive.Content
            components={{
              Text: MarkdownText,
              Image: ScreenshotPart,
              tools: { Fallback: ToolCallPart },
            }}
          />
        </div>
      </MessagePrimitive.Root>
    </MessagePrimitive.If>
  );
}
